import React, { useState } from 'react';
import { Form, Input, Button, Row, Col, Select, notification, Typography, Divider } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import {
    UserOutlined, LockOutlined, ArrowRightOutlined, PhoneOutlined,
} from '@ant-design/icons';
import api from '../api/axios';
import AuthLayout from '../layouts/AuthLayout';

const { Title, Text } = Typography;
const { Option } = Select;

const Signup = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const response = await api.post('/signup', values);
            if (response.status === 200 && response.data.status === 'success') {
                notification.success({ message: 'Account Created!', description: 'Please login to continue.' });
                navigate('/login');
            } else {
                notification.error({ message: 'Signup Failed', description: 'Please try again.' });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach((key) =>
                    notification.error({ message: 'Validation Error', description: errors[key][0] })
                );
            } else {
                notification.error({ message: 'Signup Error', description: 'An error occurred. Please try again later.' });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Create Your Free Profile"
                description="Join Oottan for free. Create your matrimony profile and start your journey to find the perfect life partner today."
                canonical="/signup"
            />
            <AuthLayout>
                <div style={{ width: '100%' }}>
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <Title level={2} style={{ color: '#1A1A2E', marginBottom: '6px', fontWeight: '800', fontSize: '1.7rem' }}>
                            Create Account
                        </Title>
                        <Text style={{ color: '#64748B', fontSize: '0.92rem' }}>
                            Join Oottan — it's free!
                        </Text>
                    </div>

                    <Form name="signup" initialValues={{ remember: true }} onFinish={handleSubmit} layout="vertical" size="large">
                        <Row gutter={12}>
                            <Col span={12}>
                                <Form.Item name="first_name" rules={[{ required: true, message: 'Required' }]}>
                                    <Input prefix={<UserOutlined style={{ color: '#AD1457' }} />} placeholder="First Name" style={{ borderRadius: '10px', height: '48px' }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="last_name" rules={[{ required: true, message: 'Required' }]}>
                                    <Input prefix={<UserOutlined style={{ color: '#AD1457' }} />} placeholder="Last Name" style={{ borderRadius: '10px', height: '48px' }} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={12}>
                            <Col span={12}>
                                <Form.Item name="gender" rules={[{ required: true, message: 'Required' }]}>
                                    <Select placeholder="Gender" style={{ height: '48px' }}>
                                        <Option value="Male">Male</Option>
                                        <Option value="Female">Female</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="mobile_number" rules={[{ required: true, message: 'Required' }]}>
                                    <Input prefix={<PhoneOutlined style={{ color: '#AD1457' }} />} placeholder="Mobile Number" style={{ borderRadius: '10px', height: '48px' }} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="email" rules={[{ required: true, message: 'Please input your email!' }]}>
                            <Input prefix={<UserOutlined style={{ color: '#AD1457' }} />} placeholder="Email Address" style={{ borderRadius: '10px', height: '48px' }} />
                        </Form.Item>

                        <Row gutter={12}>
                            <Col span={12}>
                                <Form.Item name="password" rules={[{ required: true, message: 'Required' }]}>
                                    <Input.Password prefix={<LockOutlined style={{ color: '#AD1457' }} />} placeholder="Password" style={{ borderRadius: '10px', height: '48px' }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="password_confirmation" rules={[{ required: true, message: 'Required' }]}>
                                    <Input.Password prefix={<LockOutlined style={{ color: '#AD1457' }} />} placeholder="Confirm" style={{ borderRadius: '10px', height: '48px' }} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item style={{ marginBottom: '12px' }}>
                            <Button type="primary" htmlType="submit" block loading={loading} icon={<ArrowRightOutlined />} style={{ height: '50px', borderRadius: '10px', fontSize: '1rem', fontWeight: '700', background: 'linear-gradient(135deg,#AD1457,#D81B60)', border: 'none', boxShadow: '0 6px 20px rgba(173,20,87,0.35)' }}>
                                Create Free Account
                            </Button>
                        </Form.Item>
                    </Form>

                    <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                        <Text style={{ color: '#94A3B8', fontSize: '0.78rem', lineHeight: '1.6' }}>
                            By signing up, you agree to our{' '}
                            <Link to="/terms-conditions" style={{ color: '#AD1457' }}>Terms</Link>
                            {' '}and{' '}
                            <Link to="/privacy-policy" style={{ color: '#AD1457' }}>Privacy Policy</Link>.
                        </Text>
                    </div>

                    <Divider style={{ margin: '16px 0', borderColor: '#E2E8F0' }}>
                        <Text style={{ color: '#94A3B8', fontSize: '0.82rem' }}>OR</Text>
                    </Divider>

                    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                        <Text style={{ color: '#64748B', fontSize: '0.9rem' }}>Already have an account?{' '}</Text>
                        <Link to="/login" style={{ color: '#AD1457', fontWeight: '700', fontSize: '0.9rem', textDecoration: 'none' }}>Sign in</Link>
                    </div>

                    <div style={{ textAlign: 'center', paddingTop: '12px', borderTop: '1px solid #F1F5F9' }}>
                        <Text style={{ color: '#94A3B8', fontSize: '0.78rem' }}>
                            {process.env.REACT_APP_RELEASE_DATE} · v{process.env.REACT_APP_RELEASE_VERSION}
                        </Text>
                    </div>
                </div>
            </AuthLayout>
        </>
    );
};

export default Signup;
